import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      // Navbar
      "nav_about": "About",
      "nav_skills": "Skills",
      "nav_experience": "Experience",
      "nav_education": "Education",
      "nav_contact": "Contact",
      "downloadCV": "Download CV",
      "language": "Language",

      // Hero
      "hero_greeting": "Hi, I'm",
      "hero_role": "Senior Fullstack Developer",
      "hero_summary": "I build and maintain web and mobile applications end to end — from REST APIs and MySQL databases with PHP (Laravel, CodeIgniter) and Node.js, to modern interfaces with React, Vue and Angular. I care about clean code, performance and shipping reliable features, with AI tools like Claude Code built into my daily workflow.",
      "hero_location": "Buenos Aires, Argentina",
      "age": "{{age}} years old",
      "contact_me": "Get in touch",
      "stat_years": "Years of experience",
      "stat_tech": "Technologies",
      "stat_brands": "Brands & projects",

      // Skills
      "skills_eyebrow": "Toolbox",
      "skills_title": "Skills & technologies",
      "skills_subtitle": "The stack I use day to day to design, build, test and ship products.",
      "cat_backend": "Backend",
      "cat_frontend": "Frontend",
      "cat_mobile": "Mobile",
      "cat_data": "Databases & DevOps",
      "cat_testing": "Testing & quality",
      "cat_tools": "Tools & methodologies",
      "cat_ai": "AI-assisted development",
      "languages_title": "Languages",
      "lang_en": "English",
      "lang_pt": "Portuguese",
      "level_advanced": "Advanced",
      "level_basic": "Basic",

      // Experience
      "exp_eyebrow": "Career",
      "exp_title": "Professional experience",
      "present": "Present",
      "role_sr_fullstack": "Senior Fullstack Developer",
      "role_mobile": "Mobile Application Developer",
      "role_web": "Web Developer",
      "role_dba": "Database Administrator – Web Support",
      "remote": "Remote",
      "show_more": "Show {{count}} more",
      "show_less": "Show less",
      "bullets_sr_fullstack": [
        "Backend development and maintenance of web applications with PHP (CodeIgniter, Laravel) and Node.js with Express, including unit testing with PHPUnit and Jest.",
        "Frontend development with Vue.js, Angular and React.js.",
        "Web technologies such as JavaScript, AJAX, jQuery and Bootstrap.",
        "Design, development and consumption of REST APIs.",
        "MySQL database management, query optimization and data modeling.",
        "Version control with Git, working with GitHub and GitLab.",
        "Containerized applications and development environments with Docker and Docker Compose.",
        "Claude and Claude Code as AI assistants integrated into the daily workflow to speed up feature implementation, refactoring, debugging and code review.",
        "Best development practices and performance optimization.",
        "Agile, collaborative teams working with ScrumBan."
      ],
      "bullets_mobile": [
        "Hybrid mobile apps with Ionic Framework and Capacitor.",
        "Responsive, dynamic interfaces with Angular and React.js.",
        "Backend with Node.js and Express, building and managing REST APIs.",
        "REST API integration for real-time data synchronization.",
        "Capacitor plugins for native features such as camera, geolocation and push notifications.",
        "Performance and load-time optimization on mobile.",
        "Testing and debugging on physical devices and emulators to ensure stability.",
        "Best practices in mobile development and UX/UI design."
      ],
      "bullets_web": [
        "Custom web applications and websites with PHP (Laravel, CodeIgniter) and Node.js with Express.",
        "Dynamic interfaces with Vue.js, Angular and React.js.",
        "Interactive features with JavaScript, AJAX, jQuery and Bootstrap.",
        "Design, optimization and management of MySQL databases.",
        "REST API development and integration with external services.",
        "Direct communication with clients to define requirements and provide technical support.",
        "Autonomous project management, delivering on time."
      ],
      "bullets_dba": [
        "Database administration.",
        "Web support.",
        "HTML development for email campaigns."
      ],
      "clients_title": "Brands I've worked on",
      "clients_subtitle": "Some of the companies behind projects I've contributed to.",

      // Education
      "edu_eyebrow": "Education",
      "edu_title": "Academic history",
      "type_degree": "Degree",
      "type_course": "Course",
      "edu_esba": "Higher Technical Degree in Cybersecurity and Artificial Intelligence",
      "edu_utn_fullstack": "Professional Full Stack Developer: ReactJS, Angular, NodeJS",
      "edu_uba": "Bachelor's Degree in Systems Analysis",
      "edu_udemy_react": "ReactJS",
      "edu_eit_php": "PHP & MySQL Programming",
      "edu_utn_adv": "Advanced Web Programming",
      "edu_utn_intro": "Introductory Web Programming",
      "edu_uces": "Associate Degree in Systems Programming",
      "inst_uba": "University of Buenos Aires",
      "inst_utn": "National Technological University",
      "inst_uces": "University of Business and Social Sciences (UCES)",

      // Contact / footer
      "contact_eyebrow": "Contact",
      "contact_title": "Let's build something together",
      "contact_text": "Have a project in mind or a role that fits my profile? I'd be happy to talk.",
      "send_email": "Send me an email",
      "footer_built": "Designed & built with React."
    }
  },
  es: {
    translation: {
      // Navbar
      "nav_about": "Sobre mí",
      "nav_skills": "Habilidades",
      "nav_experience": "Experiencia",
      "nav_education": "Formación",
      "nav_contact": "Contacto",
      "downloadCV": "Descargar CV",
      "language": "Idioma",

      // Hero
      "hero_greeting": "Hola, soy",
      "hero_role": "Senior Fullstack Developer",
      "hero_summary": "Desarrollo y mantengo aplicaciones web y móviles de punta a punta: desde APIs REST y bases de datos MySQL con PHP (Laravel, CodeIgniter) y Node.js, hasta interfaces modernas con React, Vue y Angular. Me enfoco en el código limpio, el rendimiento y en entregar funcionalidades confiables, con herramientas de IA como Claude Code integradas en mi flujo de trabajo diario.",
      "hero_location": "Buenos Aires, Argentina",
      "age": "{{age}} años",
      "contact_me": "Contactame",
      "stat_years": "Años de experiencia",
      "stat_tech": "Tecnologías",
      "stat_brands": "Marcas y proyectos",

      // Skills
      "skills_eyebrow": "Herramientas",
      "skills_title": "Habilidades y tecnologías",
      "skills_subtitle": "El stack que uso a diario para diseñar, desarrollar, testear y desplegar productos.",
      "cat_backend": "Backend",
      "cat_frontend": "Frontend",
      "cat_mobile": "Mobile",
      "cat_data": "Bases de datos y DevOps",
      "cat_testing": "Testing y calidad",
      "cat_tools": "Herramientas y metodologías",
      "cat_ai": "Desarrollo asistido por IA",
      "languages_title": "Idiomas",
      "lang_en": "Inglés",
      "lang_pt": "Portugués",
      "level_advanced": "Avanzado",
      "level_basic": "Básico",

      // Experience
      "exp_eyebrow": "Trayectoria",
      "exp_title": "Experiencia profesional",
      "present": "Actualidad",
      "role_sr_fullstack": "Senior Fullstack Developer",
      "role_mobile": "Desarrollador de Aplicaciones Móviles",
      "role_web": "Desarrollador Web",
      "role_dba": "Administrador de Base de Datos – Soporte Web",
      "remote": "Remoto",
      "show_more": "Ver {{count}} más",
      "show_less": "Ver menos",
      "bullets_sr_fullstack": [
        "Desarrollo backend y mantenimiento de aplicaciones web con PHP (CodeIgniter, Laravel) y Node.js con Express, incluyendo testing unitario con PHPUnit y Jest.",
        "Desarrollo de frontend con Vue.js, Angular y React.js.",
        "Manejo de tecnologías web como JavaScript, AJAX, jQuery y Bootstrap.",
        "Desarrollo y consumo de API REST.",
        "Gestión de bases de datos MySQL, optimización de consultas y modelado de datos.",
        "Control de versiones con Git, trabajando con GitHub y GitLab.",
        "Contenerización de aplicaciones y gestión de entornos de desarrollo con Docker y Docker Compose.",
        "Uso de Claude y Claude Code como asistentes de IA integrados en el flujo de desarrollo diario para agilizar la implementación de features, refactorización, debugging y revisión de código.",
        "Implementación de buenas prácticas de desarrollo y optimización de rendimiento.",
        "Trabajo en equipos ágiles y entornos colaborativos con ScrumBan."
      ],
      "bullets_mobile": [
        "Desarrollo de aplicaciones móviles híbridas con Ionic Framework y Capacitor.",
        "Interfaces responsivas y dinámicas con Angular y React.js.",
        "Backend con Node.js y Express, creando y gestionando APIs REST.",
        "Integración de APIs REST para sincronización de datos en tiempo real.",
        "Uso de Capacitor Plugins para funcionalidades nativas como cámara, geolocalización y notificaciones push.",
        "Optimización de rendimiento y tiempos de carga en aplicaciones móviles.",
        "Pruebas y depuración en dispositivos físicos y emuladores para garantizar estabilidad.",
        "Buenas prácticas en desarrollo móvil y diseño UX/UI."
      ],
      "bullets_web": [
        "Aplicaciones y sitios web a medida con PHP (Laravel, CodeIgniter) y Node.js con Express.",
        "Interfaces dinámicas con Vue.js, Angular y React.js.",
        "Funcionalidades interactivas con JavaScript, AJAX, jQuery y Bootstrap.",
        "Diseño, optimización y gestión de bases de datos MySQL.",
        "Desarrollo y consumo de API REST para integración con servicios externos.",
        "Comunicación directa con clientes para definir requisitos y brindar soporte técnico.",
        "Gestión autónoma de proyectos, asegurando entregas en tiempo y forma."
      ],
      "bullets_dba": [
        "Administración de las bases de datos.",
        "Soporte web.",
        "Creación de HTML para mailings."
      ],
      "clients_title": "Marcas con las que trabajé",
      "clients_subtitle": "Algunas de las empresas detrás de los proyectos en los que participé.",

      // Education
      "edu_eyebrow": "Formación",
      "edu_title": "Historial académico",
      "type_degree": "Carrera",
      "type_course": "Curso",
      "edu_esba": "Tecnicatura Superior en Seguridad Informática e Inteligencia Artificial",
      "edu_utn_fullstack": "Professional Full Stack Developer: ReactJS, Angular, NodeJS",
      "edu_uba": "Licenciatura en Análisis de Sistemas",
      "edu_udemy_react": "ReactJS",
      "edu_eit_php": "Programación en PHP y MySQL",
      "edu_utn_adv": "Programación Web Avanzada",
      "edu_utn_intro": "Programación Web Inicial",
      "edu_uces": "Tecnicatura en Programación de Sistemas",
      "inst_uba": "Universidad de Buenos Aires",
      "inst_utn": "Universidad Tecnológica Nacional",
      "inst_uces": "Universidad de Ciencias Empresariales y Sociales (UCES)",

      // Contact / footer
      "contact_eyebrow": "Contacto",
      "contact_title": "Construyamos algo juntos",
      "contact_text": "¿Tenés un proyecto en mente o una búsqueda que encaje con mi perfil? Hablemos.",
      "send_email": "Enviame un email",
      "footer_built": "Diseñado y desarrollado con React."
    }
  }
};

// Idioma inicial: el último elegido o, si no hay, el del navegador
function detectLanguage() {
  try {
    const saved = window.localStorage.getItem('lang');
    if (saved === 'en' || saved === 'es') return saved;
  } catch (e) {
    // localStorage puede no estar disponible (modo privado, etc.)
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return nav.toLowerCase().startsWith('es') ? 'es' : 'en';
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: detectLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  try {
    window.localStorage.setItem('lang', lng);
  } catch (e) {
    // ignorar
  }
});
document.documentElement.lang = i18n.language;

export default i18n;
